<template>
  <FormDialog
    :visible="visible"
    :title="dialogTitle"
    :width="560"
    @close="$emit('close')"
  >
    <el-form
      ref="titleForm"
      :model="form"
      :rules="rules"
      label-width="110px"
      label-position="right"
      @submit.native.prevent
    >
      <el-form-item label="抬头类型" prop="invoiceType">
        <el-radio-group v-model="form.invoiceType">
          <el-radio :label="0">个人</el-radio>
          <el-radio :label="1">企业</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="发票抬头" prop="title">
        <el-input
          v-model="form.title"
          placeholder="请输入发票抬头"
          maxlength="128"
        />
      </el-form-item>
      <template v-if="isEnterprise">
        <el-form-item label="纳税人识别号" prop="taxNo">
          <el-input
            v-model="form.taxNo"
            placeholder="请输入纳税人识别号"
            maxlength="64"
          />
        </el-form-item>
        <el-form-item label="注册地址" prop="address">
          <el-input
            v-model="form.address"
            placeholder="请输入注册地址"
            maxlength="256"
          />
        </el-form-item>
        <el-form-item label="联系电话" prop="phone">
          <el-input
            v-model="form.phone"
            placeholder="请输入联系电话"
            maxlength="32"
          />
        </el-form-item>
        <el-form-item label="开户行" prop="bankName">
          <el-input
            v-model="form.bankName"
            placeholder="请输入开户行"
            maxlength="128"
          />
        </el-form-item>
        <el-form-item label="银行账号" prop="bankAccount">
          <el-input
            v-model="form.bankAccount"
            placeholder="请输入银行账号"
            maxlength="64"
          />
        </el-form-item>
      </template>
    </el-form>

    <template slot="footer">
      <el-button
        class="indiv-btn"
        type="primary"
        size="small"
        :loading="submitting"
        @click="onSubmit"
      >
        提交
      </el-button>
    </template>
  </FormDialog>
</template>

<script>
/** 新增/编辑发票抬头弹窗 */
import { saveInvoiceTitle } from './api';

const EMPTY_FORM = {
  id: null,
  invoiceType: 1,
  title: '',
  taxNo: '',
  address: '',
  phone: '',
  bankName: '',
  bankAccount: '',
};

/** 个人抬头不需保留的企业字段 */
const ENTERPRISE_FIELD_KEYS = ['taxNo', 'address', 'phone', 'bankName', 'bankAccount'];

function emptyEnterpriseFields() {
  return {
    taxNo: '',
    address: '',
    phone: '',
    bankName: '',
    bankAccount: '',
  };
}

export default {
  name: 'InvoiceTitleDialog',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    editData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      submitting: false,
      form: { ...EMPTY_FORM },
    };
  },
  computed: {
    isEdit() {
      return this.editData && this.editData.id != null;
    },
    isEnterprise() {
      return Number(this.form.invoiceType) === 1;
    },
    dialogTitle() {
      return this.isEdit ? '编辑抬头' : '添加抬头';
    },
    rules() {
      const rules = {
        invoiceType: [
          { required: true, message: '请选择抬头类型', trigger: 'change' },
        ],
        title: [
          { required: true, message: '请输入发票抬头', trigger: ['blur', 'change'] },
        ],
      };
      if (this.isEnterprise) {
        rules.taxNo = [
          {
            required: true,
            message: '请输入纳税人识别号',
            trigger: ['blur', 'change'],
          },
        ];
      }
      return rules;
    },
  },
  watch: {
    visible(val) {
      if (!val) {
        this.resetForm();
        return;
      }
      this.fillForm(this.editData);
    },
    'form.invoiceType'(val) {
      // 企业改为个人时清空税号等企业字段，避免隐藏后仍残留数据
      if (Number(val) === 0) {
        Object.assign(this.form, emptyEnterpriseFields());
      }
      this.$nextTick(() => {
        const form = this.$refs.titleForm;
        if (form) {
          form.clearValidate(ENTERPRISE_FIELD_KEYS);
        }
      });
    },
  },
  methods: {
    fillForm(data) {
      if (data && data.id != null) {
        const invoiceType = Number(data.invoiceType) === 0 ? 0 : 1;
        this.form = {
          id: data.id,
          invoiceType,
          title: data.title || '',
          ...(invoiceType === 1
            ? {
                taxNo: data.taxNo || '',
                address: data.address || '',
                phone: data.phone || '',
                bankName: data.bankName || '',
                bankAccount: data.bankAccount || '',
              }
            : emptyEnterpriseFields()),
        };
      } else {
        this.form = { ...EMPTY_FORM };
      }
      this.$nextTick(() => {
        const form = this.$refs.titleForm;
        if (form) form.clearValidate();
      });
    },
    resetForm() {
      this.form = { ...EMPTY_FORM };
      this.submitting = false;
    },
    close() {
      this.$emit('close');
    },
    onSubmit() {
      const form = this.$refs.titleForm;
      if (!form || this.submitting) return;
      form.validate((valid) => {
        if (!valid) return;
        this.submitting = true;
        const payload = {
          invoiceType: this.form.invoiceType,
          title: String(this.form.title || '').trim(),
        };
        if (this.isEdit) {
          payload.id = this.form.id;
        }
        if (this.isEnterprise) {
          payload.taxNo = String(this.form.taxNo || '').trim();
          payload.address = String(this.form.address || '').trim();
          payload.phone = String(this.form.phone || '').trim();
          payload.bankName = String(this.form.bankName || '').trim();
          payload.bankAccount = String(this.form.bankAccount || '').trim();
        } else {
          Object.assign(payload, emptyEnterpriseFields());
        }
        saveInvoiceTitle(payload)
          .then(() => {
            this.$emit('success', { mode: this.isEdit ? 'edit' : 'add' });
            this.close();
          })
          .catch((err) => {
            this.$message.error((err && err.message) || '保存失败');
          })
          .finally(() => {
            this.submitting = false;
          });
      });
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/view/client/personal-center/panels/TitleManage/InvoiceTitleDialog.vue